import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { SilentError } from 'init/silent-error';
import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  type QueryOptions,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';

const URL = '/api_credential';

// `clientId` is optional, not nullable: the credential is generated for every
// company, so it is always a non-empty string when the public API is enabled
// and is omitted entirely when it is not (nullable XOR optional invariant).
export const ApiCredentialsSchema = z.object({
  usePublicAPI: z.boolean(),
  clientId: z.string().optional(),
});

export type ApiCredentials = z.infer<typeof ApiCredentialsSchema>;

export async function loadApiCredentialsQueryFn(axiosInstance: Promise<AxiosInstance>) {
  const instance = await axiosInstance;
  const { data } = await instance.get(URL);

  const parseResult = ApiCredentialsSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'apiCredentials');
    throw new SilentError();
  }

  return parseResult.data;
}

export function useLoadApiCredentials(options: QueryOptions<ApiCredentials> = {}) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<ApiCredentials, ApiError>({
    queryKey: ['apiCredentials'],
    queryFn: () => loadApiCredentialsQueryFn(axiosInstance),
    ...options,
  });

  return addResourceNameToQueryResult<ApiCredentials, ApiError, 'apiCredentials'>(
    'apiCredentials',
    queryResult,
  );
}
